import { subscribe } from '@jutro/events';
import { SgSessionStorageService } from 'saga-modules';
import _ from 'lodash';
import { ADOBE_TOPICS, CepAdobeAnalyticsUtil } from 'sg-adobe-analytics';
import CepConstants from './CepConstants';
import { SG_AA_PAGE_NAME, SG_CEP_MTA_SUMMARY_PAGES } from '../AdobeAnalyticsConstants';

let { tagging } = {};
let eventsSubscribed = false;
let previousPageName;

const cleanup = (event) => {
    if (!event || !SG_CEP_MTA_SUMMARY_PAGES.includes(event.pageName)) {
        delete tagging.mtaArray;
    }
    delete tagging.errorPageCode;
    delete tagging.errorPageMessage;
};

const trackPreviousPageName = () => {
    if (previousPageName) {
        tagging.previousPageName = previousPageName;
    }
};

const trackPageName = (event) => {
    cleanup(event);
    trackPreviousPageName();
    const pageName = `${SG_AA_PAGE_NAME.MY_SAGA}|${SG_AA_PAGE_NAME.MANAGE_POLICY}|${event.pageName}`;
    tagging.pageName = pageName;
    previousPageName = pageName;
};

const updateSelfServeProductsArray = (event) => {
    tagging.selfServeProducts = [];
    event.policyArray.forEach((policy) => {
        tagging.selfServeProducts.push(CepAdobeAnalyticsUtil.setPolicyDetails(policy));
    });

    event.renewalArray.forEach((renewal) => {
        tagging.selfServeProducts.push(CepAdobeAnalyticsUtil.setRenewalDetails({ value: renewal }));
    });
};

const filterJourneyProduct = (event) => {
    if (event.policyNumber && tagging.selfServeProducts) {
        tagging.selfServeProducts = tagging.selfServeProducts.filter((product) => {
            return product.policyNumber === event.policyNumber && product.type === CepConstants.PRODUCT_TYPES.CURRENT_POLICY;
        });
    }
    if (event.renewal) {
        tagging.selfServeProducts = [CepAdobeAnalyticsUtil.setRenewalDetails(event.renewal)];
    }
};

const addToChanges = (key, value, arrayKey) => {
    tagging[arrayKey].push({
        policyChange: key,
        value: value
    });
};

const handleModificationsChanges = (key, modifications, arrayKey) => {
    modifications.forEach((modification) => {
        addToChanges(key, modification, arrayKey);
    });
};

const handleCoveragesChanges = (key, coverages, arrayKey) => {
    coverages.forEach((coverage) => {
        addToChanges(key, coverage.patternCode, arrayKey);
    });
};

const setHistory = (data, arrayKey) => {
    if (data[CepConstants.VEHICLE_CHANGES.VEHICLE]) {
        addToChanges(CepConstants.POLICY_CHANGES.REPLACE_VEHICLE,
            CepAdobeAnalyticsUtil.getRegNumberValue(data[CepConstants.VEHICLE_CHANGES.VEHICLE].registration), arrayKey);
    }
    if (data[CepConstants.VEHICLE_CHANGES.REGISTRATION]) {
        addToChanges(CepConstants.POLICY_CHANGES.REG_CHANGE,
            CepAdobeAnalyticsUtil.getRegNumberValue(data[CepConstants.VEHICLE_CHANGES.REGISTRATION]), arrayKey);
    }
    if (data[CepConstants.VEHICLE_CHANGES.MILEAGE]) {
        addToChanges(CepConstants.POLICY_CHANGES.MILEAGE, data[CepConstants.VEHICLE_CHANGES.MILEAGE].code, arrayKey);
    }
    if (data[CepConstants.VEHICLE_CHANGES.VEHICLE_USE]) {
        addToChanges(CepConstants.POLICY_CHANGES.VEHICLE_USE, data[CepConstants.VEHICLE_CHANGES.VEHICLE_USE].code, arrayKey);
    }
    if (data[CepConstants.VEHICLE_CHANGES.PARKING_TYPE]) {
        addToChanges(CepConstants.POLICY_CHANGES.PARKING_TYPE, data[CepConstants.VEHICLE_CHANGES.PARKING_TYPE].code, arrayKey);
    }
    if (data[CepConstants.VEHICLE_CHANGES.ADDED_MODIFICATION]) {
        handleModificationsChanges(CepConstants.POLICY_CHANGES.ADDED_MODIFICATION, data[CepConstants.VEHICLE_CHANGES.ADDED_MODIFICATION], arrayKey);
    }
    if (data[CepConstants.VEHICLE_CHANGES.REMOVED_MODIFICATION]) {
        handleModificationsChanges(CepConstants.POLICY_CHANGES.REMOVED_MODIFICATION,
            data[CepConstants.VEHICLE_CHANGES.REMOVED_MODIFICATION], arrayKey);
    }
    if (data[CepConstants.DRIVER_CHANGES.ADDED_DRIVER]) {
        addToChanges(CepConstants.POLICY_CHANGES.ADD_DRIVER, '1', arrayKey);
    }
    if (data[CepConstants.DRIVER_CHANGES.REMOVED_DRIVER]) {
        addToChanges(CepConstants.POLICY_CHANGES.REMOVE_DRIVER, '1', arrayKey);
    }
    if (data[CepConstants.COVERAGES.ADDED]) {
        handleCoveragesChanges(CepConstants.POLICY_CHANGES.COVERAGE_ADDED, data[CepConstants.COVERAGES.ADDED], arrayKey);
    }
    if (data[CepConstants.COVERAGES.REMOVED]) {
        handleCoveragesChanges(CepConstants.POLICY_CHANGES.COVERAGE_REMOVED, data[CepConstants.COVERAGES.REMOVED], arrayKey);
    }
    if (data[CepConstants.COVERAGES.MODIFIED_VOLUNTARY_AD]) {
        addToChanges(CepConstants.POLICY_CHANGES.COVERAGE_MODIFIED_VOLUNTARY_AD,
            CepAdobeAnalyticsUtil.getVoluntaryAdValue(data[CepConstants.COVERAGES.MODIFIED_VOLUNTARY_AD]), arrayKey);
    }
    if (data[CepConstants.COVERAGES.MODIFIED_BREAKDOWN_LEVEL]) {
        addToChanges(CepConstants.POLICY_CHANGES.COVERAGE_MODIFIED_BREAKDOWN_LEVEL, data[CepConstants.COVERAGES.MODIFIED_BREAKDOWN_LEVEL], arrayKey);
    }
};

const trackMtaChanges = (event) => {
    const data = event.changes;
    if (!data) {
        return;
    }
    tagging.mtaArray = [];
    setHistory(data, 'mtaArray');
};

const trackRenewalChanges = (event) => {
    const data = event.changes;
    if (!data) {
        return;
    }
    tagging.renewalArray = [];
    setHistory(data, 'renewalArray');
};

const updateJourneyProduct = (event) => {
    if (tagging.selfServeProducts) {
        const product = tagging.selfServeProducts[0];
        const change = event.modelVM.value;

        if (product && change) {
            product.price = change.totalCost.amount;

            if (product.vehicle && product.type !== CepConstants.PRODUCT_TYPES.RENEWAL_PROPOSAL) {
                product.vehicle.make = change.lobData.privateMotor.coverables.spmVehicle.make;
                product.vehicle.model = change.lobData.privateMotor.coverables.spmVehicle.model;
                product.vehicle.registration = change.lobData.privateMotor.coverables.spmVehicle.registrationNumber;
            }
        }
    }
};

const getLoggedInStatus = () => {
    return SgSessionStorageService.getItem(SgSessionStorageService.KEY.IS_AUTHORIZED) === 'true';
};

const trackErrorPageCodeAndMessage = (event) => {
    if (_.get(event, 'baseError.error.data.appErrorCode')) {
        tagging.errorPageCode = event.baseError.error.data.appErrorCode;
        tagging.errorPageMessage = event.baseError.error.data.appError;
        return;
    }
    if (_.get(event, 'baseError.error')) {
        tagging.errorPageCode = event.baseError.error.code;
        tagging.errorPageMessage = event.baseError.error.message;
        return;
    }
    if (_.get(event, 'baseError.message')) {
        tagging.errorPageMessage = event.baseError.message;
        return;
    }
    if (_.get(event, 'customErrorMessage')) {
        tagging.errorPageMessage = event.customErrorMessage;
        return;
    }
    tagging.errorPageCode = event.appErrorCode;
};

export default {
    init(productTagging, adobeTagging) {
        tagging = adobeTagging;
        tagging.pageType = 'self-serve';
        tagging.loginStatus = getLoggedInStatus();
        if (!eventsSubscribed) {
            eventsSubscribed = true;
            subscribe([ADOBE_TOPICS.SELF_SERVE_PRODUCTS], (event) => {
                updateSelfServeProductsArray(event);
            });

            subscribe([ADOBE_TOPICS.FILTER_JOURNEY_PRODUCT], (event) => {
                filterJourneyProduct(event);
            });

            subscribe([ADOBE_TOPICS.MTA_CHANGES], (event) => {
                trackMtaChanges(event);
            });

            subscribe([ADOBE_TOPICS.RENEWAL_CHANGES], (event) => {
                trackRenewalChanges(event);
            });

            subscribe([ADOBE_TOPICS.UPDATE_JOURNEY_PRODUCT], (event) => {
                updateJourneyProduct(event, tagging);
            });

            subscribe([ADOBE_TOPICS.PAGE_NAME], (event) => {
                trackPageName(event, tagging);
            });

            subscribe([ADOBE_TOPICS.CONTACT_US_PAGE], (event) => {
                cleanup();
                trackErrorPageCodeAndMessage(event);
            });
        }
    }
};
